import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { bookingAPI, receiptAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import { ComingSoonOverlay } from '../../components/ComingSoon';
import {
  CalendarIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

function CustomerDashboard() {
  const { user } = useAuth();

  const { data: reservations, isLoading: reservationsLoading } = useQuery(
    'customer-reservations',
    () => bookingAPI.getReservations({ limit: 5 }),
    { select: (response) => response.data }
  );

  const { data: receipts, isLoading: receiptsLoading } = useQuery(
    'customer-receipts',
    () => receiptAPI.getReceipts({ limit: 3 }),
    { select: (response) => response.data }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'text-success-on-container bg-success-container';
      case 'pending': return 'text-warning-on-container bg-warning-container';
      case 'cancelled': return 'text-error-on-container bg-error-container';
      case 'completed': return 'text-info-on-container bg-info-container';
      default: return 'text-on-surface-variant bg-surface-container-low';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <CheckCircleIcon className="w-5 h-5" />;
      case 'pending': return <ClockIcon className="w-5 h-5" />;
      case 'cancelled': return <ExclamationTriangleIcon className="w-5 h-5" />;
      case 'completed': return <CheckCircleIcon className="w-5 h-5" />;
      default: return <ClockIcon className="w-5 h-5" />;
    }
  };

  return (
    <PageLayout>
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">
          Welcome back, {user?.email}
        </h1>
        <p className="mt-2 text-on-surface-variant">
          Manage your vehicle reservations and account
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ComingSoonOverlay label="Coming Soon">
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-on-surface">Book a Vehicle</h3>
                <p className="text-on-surface-variant">Reserve your next ride</p>
              </div>
            </div>
          </div>
        </ComingSoonOverlay>

        <ComingSoonOverlay label="Coming Soon">
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-on-surface">My Reservations</h3>
                <p className="text-on-surface-variant">View and manage bookings</p>
              </div>
            </div>
          </div>
        </ComingSoonOverlay>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCardIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-on-surface">Payment Methods</h3>
              <p className="text-on-surface-variant">Manage billing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Reservations */}
        <ComingSoonOverlay label="Reservations — Coming Soon">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-on-surface">Recent Reservations</h3>
                <span className="text-on-surface-variant text-sm">View all</span>
              </div>
            </div>

            <div className="p-6">
              <p className="text-on-surface-variant text-center py-8">No reservations yet</p>
            </div>
          </div>
        </ComingSoonOverlay>

        {/* Recent Receipts */}
        <div className="card">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-on-surface">Recent Receipts</h3>
              <button className="text-primary-600 hover:text-primary-700 text-sm">
                View all
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {receiptsLoading ? (
              <LoadingSpinner className="py-8" />
            ) : receipts?.length > 0 ? (
              <div className="space-y-4">
                {receipts.slice(0, 3).map((receipt: any) => (
                  <div key={receipt.id} className="flex items-center justify-between p-4 bg-surface rounded-card">
                    <div>
                      <p className="font-medium text-on-surface">
                        ${receipt.amount}
                      </p>
                      <p className="text-sm text-on-surface-variant">
                        {new Date(receipt.date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-pill ${getStatusColor(receipt.status)}`}>
                      {receipt.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-on-surface-variant text-center py-8">No receipts yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Account Status */}
      <div className="mt-8">
        <div className="card p-6">
          <h3 className="text-lg font-medium text-on-surface mb-4">Account Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface">Email Verified</p>
                <p className="text-sm text-on-surface-variant">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {user?.kyc_status === 'verified' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface">KYC Status</p>
                <p className="text-sm text-on-surface-variant capitalize">{user?.kyc_status || 'pending'}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCardIcon className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface">Payment Method</p>
                <p className="text-sm text-on-surface-variant">Ready to book</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export default CustomerDashboard;